import { useEffect, type ReactNode } from 'react';

import { CallHost } from '@/features/calls/CallHost';
import { CallProvider } from '@/features/calls/CallProvider';
import { MediaViewer } from '@/features/attachments/MediaViewer';
import { RealtimeProvider } from '@/websocket/provider';
import { useViewer } from '@/features/auth/session';

/**
 * Everything that must exist for the whole signed-in session lives here: the
 * single WebSocket connection, the call surface and the media viewer.
 *
 * Mounting them above the routes means switching between chats and settings
 * neither drops the connection nor interrupts a call in progress.
 */
/**
 * A file dropped anywhere but a drop zone must do nothing.
 *
 * The browser's default is to navigate to the dropped file, which replaces the
 * application with a picture of a cat and takes every unsent draft, the open
 * conversation and the connection with it — for a gesture that missed its
 * target by twenty pixels. Refusing the default everywhere makes the miss cost
 * nothing; the conversation cancels these events itself and accepts the file.
 */
function useRefuseStrayFileDrop(): void {
  useEffect(() => {
    const refuse = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      // A region that accepts files has already cancelled this: React's
      // listeners sit on the root element, which is inside the window.
      if (event.defaultPrevented) return;
      // 'none' draws the refusing cursor and stops the drop from being
      // performed at all; cancelling is what keeps the browser from navigating.
      event.dataTransfer.dropEffect = 'none';
      event.preventDefault();
    };
    window.addEventListener('dragover', refuse);
    window.addEventListener('drop', refuse);
    return () => {
      window.removeEventListener('dragover', refuse);
      window.removeEventListener('drop', refuse);
    };
  }, []);
}

export function AuthenticatedShell({ children }: { children: ReactNode }) {
  const viewer = useViewer();
  useRefuseStrayFileDrop();

  return (
    <RealtimeProvider viewerUserId={viewer.id}>
      <CallProvider>
        <a className="skip-link" href="#main">
          Перейти к содержимому
        </a>
        {children}
        <CallHost />
        <MediaViewer />
      </CallProvider>
    </RealtimeProvider>
  );
}
